/** A box in viewport pixels, as getBoundingClientRect reports it. */
export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

export type TourPlacement = 'right' | 'bottom';

/** Kept clear between the card and the viewport edge. */
export const EDGE = 16;
/** Between the highlighted element and the card. */
export const GAP = 14;
/** Breathing room the highlight keeps around its element. */
export const HALO = 6;

/**
 * The hole cut into the scrim. With no target it collapses to a point in the middle of the
 * screen, so the scrim covers everything and the card can sit centred over it.
 */
export function spotlightBox(target: Box | null, viewportWidth: number, viewportHeight: number): Box {
  if (!target) return { left: viewportWidth / 2, top: viewportHeight / 2, width: 0, height: 0 };
  return {
    left: target.left - HALO,
    top: target.top - HALO,
    width: target.width + HALO * 2,
    height: target.height + HALO * 2
  };
}

/**
 * Where the card goes: on the preferred side of the highlight when it fits there, else below
 * it, else above it, else centred. Whatever the side, the card stays inside the viewport.
 */
export function cardPosition(
  highlight: Box | null,
  cardWidth: number,
  cardHeight: number,
  viewportWidth: number,
  viewportHeight: number,
  placement: TourPlacement
): { left: number; top: number } {
  const clampLeft = (x: number) => clamp(x, EDGE, viewportWidth - cardWidth - EDGE);
  const clampTop = (y: number) => clamp(y, EDGE, viewportHeight - cardHeight - EDGE);
  const centred = { left: clampLeft((viewportWidth - cardWidth) / 2), top: clampTop((viewportHeight - cardHeight) / 2) };

  if (!highlight || highlight.width === 0) return centred;

  const right = highlight.left + highlight.width;
  const bottom = highlight.top + highlight.height;

  if (placement === 'right' && right + GAP + cardWidth + EDGE <= viewportWidth) {
    return { left: right + GAP, top: clampTop(highlight.top + highlight.height / 2 - cardHeight / 2) };
  }
  if (bottom + GAP + cardHeight + EDGE <= viewportHeight) {
    return { left: clampLeft(highlight.left), top: bottom + GAP };
  }
  if (highlight.top - GAP - cardHeight >= EDGE) {
    return { left: clampLeft(highlight.left), top: highlight.top - GAP - cardHeight };
  }
  return centred;
}

/** Lower bound wins: a card wider than the viewport hugs the left edge rather than running off it. */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}
